import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";

export default function Workspace() {
  const { t } = useLanguage();
  return (
    <LongPage
      breadcrumb={[{ name: "Products", to: "/#modules" }]}
      eyebrow={t("Workspace")}
      title={t("End the radio-and-WhatsApp factory.")}
      seoTitle="Andon, Issues, Daily Checks, Shift Handover — BEEDIGIT Workspace"
      seoDescription="Andon, cross-departmental issues, daily checks and shift handover — one shop-floor coordination layer that finally replaces radios and WhatsApp groups. Book a demo."
      description={t("BEEDIGIT Workspace is the coordination layer above your factory. Andon calls land on the right phone in seconds. Cross-departmental issues get owners and SLAs. Daily checks and 5S get done. Shift handover writes itself. Everyone sees the same shop floor, in the same place.")}
      bullets={[
        "Andon calls to the right person in seconds",
        "Cross-departmental issues with owners and SLAs",
        "Daily checks and 5S on the BEEPAD tablet",
        "Shift handover generated automatically",
      ]}
      schema="product"
      primaryCta={{ label: "Book a 30-min demo", to: "/get-started" }}
      secondaryCta={{ label: "Talk to expert", to: "/get-started" }}
      ctaHeading="Stop running the plant on radios and group chats."
      ctaBody="Show us one shift. In 30 minutes we'll show you how andon, issues and handover live in one place — and how every minute of response time turns into output."
      sections={[
        {
          kind: "personas",
          eyebrow: "Who it's for",
          heading: "If you spend the shift coordinating, this is for you",
          intro: "BEEDIGIT Workspace replaces the radio, the WhatsApp group, the paper handover and the issue spreadsheet with one coordination layer everyone uses.",
          items: [
            { role: "COO", pain: "Every plant coordinates differently — and badly.", value: "One coordination standard, every site, every shift." },
            { role: "Plant Manager", pain: "Half the issues never reach me until they're already costing money.", value: "Every escalation visible, owned, with an SLA — before it bites." },
            { role: "Production Manager", pain: "Andon means shouting across the line.", value: "Andon call → right phone → response time on the clock." },
            { role: "Maintenance / Quality", pain: "I get pulled in late, when the line is already down.", value: "Triaged in seconds, with context — show up informed." },
            { role: "Shift Supervisor", pain: "Handover is a sticky note and a phone call.", value: "Shift handover generated automatically — nothing falls through." },
            { role: "HSE / 5S", pain: "Daily checks live in a binder nobody opens.", value: "Checks on BEEPAD — done, signed, traceable, gated when they aren't." },
          ],
        },
        {
          kind: "prose",
          eyebrow: "The problem",
          heading: "Your factory runs on radios, WhatsApp and sticky notes.",
          body:
            "In most plants, coordination is invisible work. Andon is a shout across the line. Issues land in a WhatsApp group that nobody owns. Maintenance gets called five minutes too late. Quality gets called twenty minutes too late. Shift handover is a sticky note and a phone call, and half of what happened last night doesn't make it to the morning team.\n\nMeanwhile, response time is the difference between a 5-minute stop and a 90-minute one — and nobody is measuring it.\n\nBEEDIGIT Workspace makes coordination a real workflow: andon to the right phone, issues with owners and SLAs, daily checks gated and signed, shift handover generated automatically. The plant stops running on tribal knowledge and starts running on a system.",
        },
        {
          kind: "steps",
          eyebrow: "How it works",
          heading: "From chat to coordinated in three steps",
          intro: "No new app for operators to learn — BEEPAD they already use, plus notifications on the phone they already carry.",
          items: [
            { n: 1, title: "We map", body: "Andon types, escalation paths, issue categories and handover templates are configured around how your plant actually works." },
            { n: 2, title: "Operators signal", body: "One tap on BEEPAD raises an andon, opens an issue, signs a daily check or starts handover. The right person gets notified in seconds." },
            { n: 3, title: "Leadership sees", body: "Response time, open issues, daily-check compliance and handover quality — visible in real time. Coordination becomes a measurable practice." },
          ],
        },
        {
          kind: "features",
          eyebrow: "What you get",
          heading: "Everything to coordinate a shift, in one place",
          cols: 3,
          items: [
            { title: "Andon", desc: "Operators raise calls from BEEPAD; the right person gets a push in seconds, with full context." },
            { title: "Escalation paths", desc: "If nobody responds in N minutes, the next level gets notified — automatically." },
            { title: "Issue tracker", desc: "Cross-departmental issues with owners, SLAs, comments, attachments and outcomes." },
            { title: "Daily checks", desc: "Routine checks and 5S audits on BEEPAD — mandatory, signed, traceable." },
            { title: "Shift handover", desc: "One-page handover generated automatically. Nothing falls through." },
            { title: "Response-time KPIs", desc: "How fast did maintenance respond? Quality? Supervision? On the clock." },
            { title: "Notifications", desc: "Push, SMS, email — to the role on duty, not the named individual. Schedule-aware." },
            { title: "Audit trail", desc: "Every call, every response, every signature — logged with user and timestamp." },
            { title: "Your stack", desc: "Push issues and events into Jira, ServiceNow, Microsoft Teams, Slack — out of the box." },
          ],
        },
        {
          kind: "callout",
          eyebrow: "Live in weeks",
          heading: "Your first coordinated shift — in 14 days.",
          body: "We configure andon, issues and handover for one production area, train operators in 30 minutes, and you're running a coordinated shift within two weeks. Scale plant-wide from there.",
          href: "/platform",
          linkLabel: "See the platform",
        },
        {
          kind: "gains",
          eyebrow: "What it's worth",
          heading: "What every role gets back",
          intro: "Coordination becomes a measurable practice — and response time becomes a controllable KPI.",
          items: [
            { role: "COO", cares: "Consistency across plants.", gain: "Same coordination standard, every site — close the worst-plant gap.", kpis: "Andon response time, open-issue SLA, handover quality.", pitch: "Make coordination portable across sites." },
            { role: "Plant Manager", cares: "Visibility.", gain: "Every issue, every andon, every handover — visible in real time.", kpis: "Open issues, escalations, response time.", pitch: "Stop hearing about problems on Monday." },
            { role: "Production Manager", cares: "Response time.", gain: "Andon to the right phone in seconds — every minute saved is output.", kpis: "Andon response time, andon-to-resolution.", pitch: "Turn coordination into output." },
            { role: "Maintenance", cares: "Triage with context.", gain: "Show up to the line knowing what's wrong — not what was shouted.", kpis: "MTTR, first-time-fix rate.", pitch: "Triage in seconds, fix in minutes." },
            { role: "Quality", cares: "Catching issues early.", gain: "NCR-adjacent issues raised at the source — not discovered in the warehouse.", kpis: "NCR cycle time, escape rate.", pitch: "Catch quality issues at the line." },
            { role: "HSE / 5S", cares: "Check compliance.", gain: "Daily checks done, signed and traceable — mandatory steps gated.", kpis: "Check compliance, 5S audit score, incident rate.", pitch: "Make the binder a workflow." },
            { role: "CIO", cares: "Tool sprawl.", gain: "One coordination layer — connected to Jira, ServiceNow, Teams, Slack.", kpis: "Tool count, integration uptime.", pitch: "Replace 5 chat groups with one system." },
          ],
        },
        {
          kind: "useCases",
          eyebrow: "In the real world",
          heading: "What changes on Monday morning",
          items: [
            { persona: "The operator", scenario: "Sees a quality issue on the line. Taps andon on BEEPAD with the type and a photo.", outcome: "QA technician arrives in 4 minutes, not 40. Bad batch contained." },
            { persona: "The supervisor", scenario: "Opens the shift dashboard. Three open issues, two near SLA, one resolved.", outcome: "Acts on the right one — before the SLA breach." },
            { persona: "The maintenance lead", scenario: "Gets an andon push: \"Line 3 — packing jam\". Photo attached.", outcome: "Arrives with the right tool. First-time fix." },
            { persona: "The shift supervisor", scenario: "Ends the shift. Handover sheet is generated: open issues, andon log, key events.", outcome: "Morning team starts informed. Nothing falls through." },
            { persona: "The HSE lead", scenario: "Pulls daily-check compliance. One station at 78%. Drills in to the missed checks.", outcome: "Coaching conversation with data, not opinions." },
            { persona: "The plant manager", scenario: "Sees andon response time creeping up on the night shift.", outcome: "Re-balances coverage. Output stops slipping." },
          ],
        },
        {
          kind: "mockups",
          eyebrow: "See it",
          heading: "Coordination in one place — finally",
          intro: "Built for the operator at the line, the supervisor on the floor and the manager on the phone. Same data, three views.",
          items: [
            { alt: "Andon board", caption: "Every active call, every response time, every escalation — visible to the whole shift." },
            { alt: "Issue tracker", caption: "Cross-departmental issues with owners, SLAs and outcomes. Nothing dies in WhatsApp." },
            { alt: "Daily checks", caption: "5S, safety and routine checks on BEEPAD — done, signed, traceable, gated when they aren't." },
            { alt: "Shift handover sheet", caption: "Generated automatically. Open issues, andon log, key events. Morning team starts informed." },
          ],
        },
        {
          kind: "kpi",
          eyebrow: "The numbers that matter",
          heading: "Four KPIs leadership will actually look at",
          intro: "Live. Drillable to the call. Exportable to your ops review.",
          items: [
            { value: "Response", label: "Andon to-arrival time" },
            { value: "Resolution", label: "Issue cycle time" },
            { value: "Compliance", label: "Daily checks done" },
            { value: "Handover", label: "Quality, per shift" },
          ],
        },
        {
          kind: "split",
          eyebrow: "Why BEEDIGIT Workspace",
          heading: "What you have today vs what you'll have next month",
          leftTitle: "Today",
          rightTitle: "With BEEDIGIT Workspace",
          left: [
            "Andon = shouting across the line",
            "Issues live in WhatsApp groups nobody owns",
            "Maintenance gets called late, with no context",
            "Daily checks in a binder nobody opens",
            "Shift handover is a sticky note and a phone call",
            "Response time is invisible — and unmanaged",
          ],
          right: [
            "Andon → right phone, in seconds, with context",
            "Issues tracked with owners, SLAs and outcomes",
            "Maintenance and quality arrive informed",
            "Daily checks on BEEPAD — gated and signed",
            "Shift handover generated automatically",
            "Response time on the clock — a controllable KPI",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Hardware included",
          heading: "Tablets, andon stations, notifications",
          intro: "BEEPAD on the floor, push notifications on the phone — provisioned and ready.",
          items: [
            "BEEPAD operator tablet",
            "BEEDIGIT HUB",
            "Andon button kit (optional)",
            "Push · SMS · Email notifications",
            "GSM · Wi-Fi · Ethernet",
            "Encrypted MQTT over TLS",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Plays well with your stack",
          heading: "Your ERP, your tickets, your chat",
          intro: "Push events and issues into the systems your business already runs on.",
          items: [
            "factory.online", "SAP", "Oracle EBS / Fusion", "Microsoft Dynamics", "Odoo",
            "Jira", "ServiceNow", "Microsoft Teams", "Slack",
            "Power BI", "Tableau", "REST / Webhooks", "MQTT",
          ],
        },
        {
          kind: "split",
          eyebrow: "Your rollout",
          heading: "From chat to coordinated in 90 days",
          leftTitle: "Weeks 1–4",
          rightTitle: "Weeks 5–12",
          left: [
            "Map andon types, escalation paths and issue categories",
            "BEEPAD live for one production area",
            "First coordinated shift — andon, issues, handover on system",
            "First response-time KPIs — and your first \"we had no idea\" moment",
          ],
          right: [
            "Roll out plant-wide",
            "Daily checks and 5S workflows enabled",
            "Integration to Teams / Slack / Jira / ServiceNow as needed",
            "Coordination KPIs in the weekly ops review — for good",
          ],
        },
        {
          kind: "callout",
          eyebrow: "Officially certified",
          heading: "The only MES certified by INCIT for raising your SIRI index",
          body: "Shop-floor coordination and workforce orchestration score in SIRI. BEEDIGIT is the first platform officially recognized for moving the needle on the Smart Industry Readiness Index.",
          href: "/siri",
          linkLabel: "See how BEEDIGIT raises SIRI",
        },
        {
          kind: "faq",
          eyebrow: "FAQ",
          heading: "What buyers ask us first",
          items: [
            { q: "How is this different from a WhatsApp group?", a: "WhatsApp doesn't know who's on shift, doesn't enforce SLAs, doesn't log response time, doesn't generate a handover, and doesn't survive an audit. Workspace does all of those — and integrates with the rest of your shop-floor data." },
            { q: "Do operators need a new app?", a: "No — it's part of BEEPAD, which they already use for execution and quality. Andon, issues, checks and handover live alongside the job order." },
            { q: "Can we integrate with Teams, Slack, Jira, ServiceNow?", a: "Yes, out of the box. Issues can flow in either direction, andon events push notifications to the right channel, and SLAs are enforced regardless of where the action happens." },
            { q: "Does it work for night and weekend shifts?", a: "Yes — escalation paths are schedule-aware. Notifications go to the role on duty, not the named individual. Coverage gaps surface as a KPI." },
            { q: "What about safety / HSE workflows?", a: "Daily checks, 5S audits and incident reports run through the same workflow as andon and issues — with mandatory gating, signatures and full audit trail." },
            { q: "Will leadership actually look at the data?", a: "Yes — response time and open-issue SLAs become a weekly ops-review KPI for our customers. When coordination becomes measurable, leadership pays attention." },
            { q: "Will it connect to our ERP and BI?", a: "Out of the box — SAP, Oracle, Microsoft Dynamics, Odoo, Power BI, Tableau. Plus REST/Webhooks for anything else." },
            { q: "What does it cost?", a: "Priced per connected user, hardware included. Most customers see payback inside two quarters from response-time improvement alone. Book the demo — we'll build the business case." },
          ],
        },
        {
          kind: "related",
          eyebrow: "Explore the platform",
          heading: "Pairs naturally with",
          items: [
            { title: "Monitoring & OEE", href: "/modules/oee-downtime", desc: "Live machine status feeds andon — every stop becomes a coordinated response." },
            { title: "Manufacturing Execution", href: "/modules/mes", desc: "Job-order context for every andon, issue and handover." },
            { title: "Quality Management", href: "/modules/quality-management", desc: "NCR-adjacent issues raised at the line and tracked to closure." },
            { title: "CMMS", href: "/modules/cmms-maintenance", desc: "Work requests from andon flow directly into the maintenance backlog." },
          ]
        },
      ]}
    />
  );
}